import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { Button, ErrorState, Skeleton } from '@keboola/design';
import { Boxes, CircleUser, KeyRound, Users } from '@keboola/design/icons';

import { session } from '../api';
import { useAuth } from '../auth/context';
import { DashboardChrome, type DashboardNavItem } from '../components/DashboardChrome';
import { EnableMfaGate } from '../features/vendors/components/EnableMfaGate';
import { SelectVendorGate } from '../features/vendors/components/SelectVendorGate';
import { resolveVendorGate } from '../features/vendors/gate';
import { useProfile } from '../features/vendors/queries';

/**
 * `/~` — the vendor dashboard root.
 *
 * - The tilde is what existing bookmarks, docs and invitation emails link to.
 */
const NAV: readonly DashboardNavItem[] = [
  { to: '/~', label: 'Components', icon: Boxes },
  { to: '/~/developers', label: 'Developers', icon: Users },
  { to: '/~/service-accounts', label: 'Service accounts', icon: KeyRound },
  { to: '/~/profile', label: 'Profile', icon: CircleUser },
];

export const Route = createFileRoute('/~')({
  /**
   * Reads the session directly, not via `useAuth`: `beforeLoad` runs before any component mounts,
   * so an unauthenticated visitor never renders dashboard chrome.
   */
  beforeLoad: ({ location }) => {
    if (session.get() === null) {
      throw redirect({ to: '/auth/login', search: { redirect: location.href } });
    }
  },
  component: VendorLayout,
});

function VendorLayout() {
  const { identity } = useAuth();
  const { data: profile, isLoading, isError, error, refetch } = useProfile();

  // Gate ordering lives in `resolveVendorGate`, where it is tested.
  const gate = resolveVendorGate({
    vendors: identity?.vendors ?? [],
    mfa: profile?.mfa,
  });

  if (isLoading) {
    return (
      <DashboardChrome section="vendor" nav={[]}>
        <Skeleton className="tw:mx-auto tw:h-64 tw:max-w-lg" />
      </DashboardChrome>
    );
  }

  // A failed profile read must not reach the gate: `resolveVendorGate` reads `mfa: undefined` as
  // "still loading" and lets it past, so a never-enrolled user would land in the dashboard.
  if (isError) {
    return (
      <DashboardChrome section="vendor" nav={[]}>
        <ErrorState>
          <ErrorState.Title as="h1">Could not load your profile</ErrorState.Title>
          {error instanceof Error && (
            <ErrorState.Description>{error.message}</ErrorState.Description>
          )}
          <ErrorState.Actions>
            <Button variant="secondary" onClick={() => void refetch()}>
              Try again
            </Button>
          </ErrorState.Actions>
        </ErrorState>
      </DashboardChrome>
    );
  }

  if (gate !== null) {
    return (
      // No nav while gated: links behind the gate would bounce straight back.
      <DashboardChrome section="vendor" nav={[]}>
        {gate === 'mfa' ? <EnableMfaGate /> : <SelectVendorGate />}
      </DashboardChrome>
    );
  }

  return (
    <DashboardChrome section="vendor" nav={NAV}>
      <Outlet />
    </DashboardChrome>
  );
}
